import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Backdrop, Card, Checkbox, FormControlLabel, ImageList, ImageListItem } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CardMedia from '@mui/material/CardMedia';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import classes from './ExpenseAttachment.module.scss';
import { useWindowSize } from '../../../../../utils/hooks';
import { appConfigVariableValues, imageFileTypes, responsiveSizeTimesheet } from '../../../../../utils/constants';
import MainButton from '../../../../common/buttons/MainButton';
import placeholderImage from '../../../../../Assets/images/expenses/placeholderAttachmentImage.svg';
import { tneAxiosClient } from '../../../../../api/apiSettings';
import { basicErrorHandler } from '../../../../../utils/helpers';
import colors from '../../../../../scss/colors';
import LoadingWithSpinner from '../../../../common/LoadingWithSpinner/LoadingWithSpinner';
import CustomDocViewer from '../../../../common/CustomDocViewer/CustomDocViewer';
import CommonFileUploader from '../../../../fileupload/CommonFileUploader/CommonFileUploader';

export default function ExpenseAttachment({
  data,
  handleCloseAttachment,
  saveAttachment,
  isEdit,
  deleteAttachment,
  savingFiles,
  isApprover
}) {
  const size = useWindowSize();
  const { t } = useTranslation();
  const [opening, setOpening] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filetoDelete, setFileToDelete] = useState();
  const [filesToShow, setFilesToShow] = useState(data?.ExpenseAttachments);
  const [hasChanges, setHasChanges] = useState(false);
  const [localSaving, setLocalSaving] = useState(false);
  const [checked, setChecked] = useState(false);
  const [pending, setPending] = useState(false);
  const [attachment, setAttachment] = useState({
    show: false,
    id: '',
    filename: '',
    data: ''
  });
  const dispatch = useDispatch();

  const uploadHandler = (files) => {
    setFilesToShow([...data.ExpenseAttachments, ...files]);
    setSelectedFiles([...files]);
    setHasChanges(true);
  };

  const handleChecked = (e) => {
    setChecked(e.target.checked);
    setHasChanges(true);
    if (e.target.checked) {
      setSelectedFiles([]);
    }
  };

  const closeItem = async (file) => {
    try {
      setFilesToShow(filesToShow.filter((fl) => fl.Id !== file.Id));
      setFileToDelete(file);
      setHasChanges(true);
      if (file?.Id) {
        setLocalSaving(true);
        const deleted = await tneAxiosClient.delete(`v1/expense/attachment/${file?.Id}`);
        if (deleted.status === 200) {
          setLocalSaving(false);
        }
      }
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
  };

  const handleAttachmentImage = (file) => {
    const { DOMAIN, BLOB_CONTAINER, SASS_TOKEN } = appConfigVariableValues;
    const filepath = file?.FileAttachmentPath;
    const blobUrlForAttachment = `${DOMAIN}/${BLOB_CONTAINER}/${filepath}${SASS_TOKEN}`;
    const fileExtension = filepath?.split('.')?.pop()?.toLowerCase();
    const isFileAnImage = imageFileTypes.includes(fileExtension);

    if (file && file?.type?.includes('image')) {
      return URL.createObjectURL(file);
    }

    if (filepath && isFileAnImage) {
      return blobUrlForAttachment;
    }

    return placeholderImage;
  };

  const handleAttachmentViewer = async (file) => {
    try {
      if (file?.Id) {
        setOpening(true);
        const result = await tneAxiosClient.get(`v1/expense/download-attachment?expenseAttachmentId=${file?.Id}`);
        if (result.status === 200) {
          setAttachment({
            show: true,
            id: file?.Id,
            filename: file?.FileAttachmentName,
            data: result?.data?.Data
          });
          setOpening(false);
        }
      }
    } catch (e) {
      setOpening(false);
      basicErrorHandler(dispatch, e.message);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = attachment.data;
    link.download = attachment.filename;
    link.click();
  };

  const handleSave = () => {
    saveAttachment(checked, selectedFiles);
    setSelectedFiles([]);
    if (filetoDelete) deleteAttachment(filetoDelete);
    setFileToDelete({});
    setHasChanges(false);
    handleCloseAttachment();
  };

  const handleButtonDisable = () => {
    if (selectedFiles?.length === 0 && !checked && !hasChanges) return true;

    return false;
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        {size.width > responsiveSizeTimesheet && (
          <div role="presentation" onClick={() => handleCloseAttachment()} className={classes.close}>
            <CloseIcon fontSize="inherit" />
          </div>
        )}
      </div>
      <p className={classes.Label}>
        {isApprover ? t('dashboard.expenses.table.receipts') : t('dashboard.expenses.table.myreceipt')}
      </p>

      <div className={classes.cardContainer}>
        <ImageList sx={{ width: '100%', maxHeight: 335, height: '100%' }} cols={3} rowHeight={164}>
          {pending && (
            <Backdrop>
              <LoadingWithSpinner />
            </Backdrop>
          )}
          {filesToShow.map((file, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <ImageListItem key={file.lastModified + idx}>
              <Card
                key={`${file.size}${file.name || file.FileAttachmentName}`}
                sx={{ margin: 1, height: '170px', width: '170px', position: 'relative', borderRadius: '10px' }}
              >
                {isEdit && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      zIndex: 1,
                      background: 'rgba(0, 0, 0, 0.2)',
                      color: 'white',
                      margin: '10px 10px',
                      width: '26px',
                      height: '26px'
                    }}
                    onClick={() => closeItem(file)}
                  >
                    <CloseIcon sx={{ width: '16px' }} />
                  </IconButton>
                )}
                <CardMedia
                  onClick={() => handleAttachmentViewer(file)}
                  sx={{ height: '200px', cursor: 'pointer' }}
                  image={handleAttachmentImage(file)}
                  title={file.name || file.FileAttachmentName}
                />
              </Card>
              <p className={classes.attachmentName}>{file.name || file.FileAttachmentName}</p>
            </ImageListItem>
          ))}
        </ImageList>
      </div>
      {isEdit && (
        <CommonFileUploader
          uploadHandler={uploadHandler}
          acceptedFormats={['.jpeg', '.jpg', '.png', '.pdf', '.doc', '.docx', '.csv', '.xls', 'xlsx']}
          selectedFiles={selectedFiles}
          maxFileSizeMB={5}
          allowedQtyOfAttachments={10}
        />
      )}
      {filesToShow?.length === 0 && selectedFiles?.length === 0 && (
        <div className={classes.checkboxContainer}>
          <FormControlLabel
            control={<Checkbox checked={checked} onChange={handleChecked} />}
            label={t('dashboard.createclaim.steps.noreceipt')}
            sx={{ margin: 0, 'MuiFormControlLabel-label': { fontSize: 9 } }}
          />
        </div>
      )}
      {isEdit && (
        <div className={classes.attachmentsButton}>
          <MainButton
            disabled={handleButtonDisable()}
            variant={handleButtonDisable() ? 'disabled' : 'primary'}
            forceWidth="204px"
            action={() => handleSave(checked)}
            sx={{ padding: '0 50' }}
            rounded
          >
            {savingFiles || localSaving ? (
              <CircularProgress size={20} sx={{ color: colors.WHITE }} />
            ) : (
              t('common.general.save')
            )}
          </MainButton>
        </div>
      )}

      {(attachment.show || opening) && (
        <CustomDocViewer
          settings={attachment}
          handleDownload={handleDownload}
          close={() =>
            setAttachment({
              show: false,
              id: '',
              filename: '',
              data: null
            })
          }
          isLoading={opening}
        />
      )}
    </div>
  );
}
